/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { ReactNode, useEffect } from 'react'
import dynamic from 'next/dynamic'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import useDatagrailPerformanceConsent from './use-datagrail-performance-consent'
import initializePosthog from './initialize-posthog'

// Dynamic imports ensure we can maintain the ability to render pages server-side
// https://posthog.com/tutorials/nextjs-app-directory-analytics#capturing-pageviews-on-the-client-side
const SuspendedAppRouterPageView = dynamic(
	() => import('./app-router-page-view'),
	{
		ssr: false,
	},
)

// PostHog is always initialized, but the tracking mode depends on the user's
// performance-cookie consent from DataGrail.
//
// - If the user accepts performance cookies, we call `posthog.opt_in_capturing()`
//   and PostHog runs in full cookie mode.
//
// - If the user explicitly rejects performance cookies, we call
//   `posthog.opt_out_capturing()` and PostHog switches to cookieless mode.
//
// - If the user hasn't made a choice yet (`null`), we do NOT call opt in or
//   opt out. PostHog remains in its default "waiting" state, which does not
//   write cookies or fully track until consent is provided.
//
// The PostHogProvider is always rendered so components can use PostHog
// hooks. Consent only changes *how* PostHog tracks, not whether the hooks work.

export default function PostHogProviderAppRouter({
	children,
}: {
	children: ReactNode
}) {
	const categoryPerformanceConsent = useDatagrailPerformanceConsent()

	// 1) Init PostHog once
	useEffect(() => {
		initializePosthog('appRouter')
	}, [])

	// 2) Switch between cookie vs cookieless based on consent changes
	useEffect(() => {
		if (categoryPerformanceConsent === true) {
			posthog.opt_in_capturing()
		} else if (categoryPerformanceConsent === false) {
			posthog.opt_out_capturing()
		}
	}, [categoryPerformanceConsent])

	return (
		<PostHogProvider client={posthog}>
			<SuspendedAppRouterPageView />
			{children}
		</PostHogProvider>
	)
}
